/**
 * showToast — thin wrapper over the global notificationStore.
 *
 * The <Toaster /> (react-hot-toast) was removed from main.tsx in favour of
 * the custom <Notifications /> component (bottom-left, reads notificationStore).
 * Keeping the same showToast API surface means all 377 call sites continue to
 * work without modification. Internally we now delegate every call to
 * notificationStore instead of react-hot-toast, which had no renderer.
 *
 * The `Toaster` named export is kept as a no-op component for backward compat
 * (imported by the old tests; removing it would require touching every import).
 *
 * BUG FIX (fix/human-create-button): showToast.error() was silently swallowed
 * because no <Toaster /> was mounted. This meant mutation errors (e.g. 422 from
 * the POST /api/v1/actors call when creating a Human actor) produced zero UI
 * feedback — the button appeared to do nothing. Routing through notificationStore
 * fixes this for all 377 call sites, not just the Human create path.
 */

import type { ReactNode } from 'react';
import { useNotificationStore } from '@/stores/notificationStore';

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface ToastOptions {
  title?: string;
  description?: string;
  /** Actions are not supported by the current Notifications renderer.
   *  Pass them and they are silently ignored. */
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Collapse a ToastOptions object into a plain string suitable for
 * notificationStore's `message` field.
 */
function resolveMessage(options: ToastOptions | string): string {
  if (typeof options === 'string') return options;
  // Prefer description as body; title is a short label.
  // If only title is provided use it as the message.
  if (options.description) return options.description;
  return options.title ?? '';
}

function resolveTitle(options: ToastOptions | string): string | undefined {
  if (typeof options === 'string') return undefined;
  // Only set title when BOTH title AND description are present (title = heading).
  return options.description ? options.title : undefined;
}

// ─── Backward-compat no-op Toaster export ────────────────────────────────────

/**
 * No-op component — kept so any remaining `import { Toaster }` statements
 * don't need to be updated. The real rendering is done by <Notifications />.
 */
export const Toaster = () => null;

// ─── showToast ────────────────────────────────────────────────────────────────

export const showToast = {
  success: (options: ToastOptions | string) => {
    useNotificationStore.getState().push({
      type: 'success',
      message: resolveMessage(options),
      title: resolveTitle(options),
    });
  },

  error: (options: ToastOptions | string) => {
    useNotificationStore.getState().push({
      type: 'error',
      message: resolveMessage(options),
      title: resolveTitle(options),
    });
  },

  info: (options: ToastOptions | string) => {
    useNotificationStore.getState().push({
      type: 'info',
      message: resolveMessage(options),
      title: resolveTitle(options),
    });
  },

  warning: (options: ToastOptions | string) => {
    useNotificationStore.getState().push({
      type: 'warning',
      message: resolveMessage(options),
      title: resolveTitle(options),
    });
  },

  /**
   * Promise toast — shows loading/success/error states.
   * Implemented via manual state pushes because notificationStore does not
   * have a native promise API.
   */
  promise: <T,>(
    promise: Promise<T>,
    msgs: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: any) => string);
    },
  ): Promise<T> => {
    const loadingId = useNotificationStore.getState().push({
      type: 'info',
      message: msgs.loading,
      duration: null, // sticky until resolved
    });
    return promise
      .then((data) => {
        useNotificationStore.getState().dismiss(loadingId);
        const msg = typeof msgs.success === 'function' ? msgs.success(data) : msgs.success;
        useNotificationStore.getState().push({ type: 'success', message: msg });
        return data;
      })
      .catch((err) => {
        useNotificationStore.getState().dismiss(loadingId);
        const msg = typeof msgs.error === 'function' ? msgs.error(err) : msgs.error;
        useNotificationStore.getState().push({ type: 'error', message: msg });
        throw err;
      });
  },

  /**
   * Custom toast — not supported by the Notifications renderer.
   * Falls back to an info notification with a best-effort string.
   */
  custom: (content: ReactNode, _options?: unknown) => {
    useNotificationStore.getState().push({
      type: 'info',
      message: typeof content === 'string' ? content : '[custom notification]',
    });
  },

  dismiss: (toastId?: string) => {
    if (toastId) {
      useNotificationStore.getState().dismiss(toastId);
    } else {
      useNotificationStore.getState().clearAll();
    }
  },
};
